/**
 * GameActions - handles action execution and processing
 *
 * Converted to async/await model. Actions are processed with proper globals
 * stack scoping. Conditions are tested and executed inline rather than through
 * the ActionCondition wrapper class.
 */

import { Action } from '../models/Action';
import { Command } from '../models/Command';
import { RagsObject } from '../models/Object';
import { Finder } from '../utils/Finder';
import { Globals } from '../core/Globals';
import { gameController } from '../core/GameController';
import { Settings } from '../core/Settings';
import { Interactables } from '../utils/Utilities';
import { objecttostring, objectToString } from '../utils/Utilities';
import { $dom } from '../utils/DOM';

declare const TheGame: any;

// Forward declarations
declare namespace GameConditions {
    function testConditionAsync(condition: any, action: any, obj: any): Promise<boolean>;
}
declare namespace GameCommands {
    function runCommandsAsync(commands: any[]): Promise<void>;
}
declare namespace GameUI {
    function clearInputChoices(): void;
    function addInputChoice(action: Action, text: string, value: any): void;
    function addOpenedObjects(obj: RagsObject, container: any, className: string): void;
    function addCharacterOptions(action: Action, withObjects?: boolean): void;
    function addObjectOptions(action: Action): void;
    function setInputMenuTitle(action: Action): void;
}
declare namespace Logger {
    function logExecutingAction(action: Action): void;
}
declare function PerformTextReplacements(text: string, obj?: any): string;
declare function SetBorders(): void;

export const GameActions = {
    executeAction: function (act: Action, bTimer?: boolean): void {
        void this.executeActionAsync(act, bTimer);
    },

    processAction: function (action: Action | string, bTimer?: boolean, objectBeingActedUpon?: any, callback?: () => void): void {
        void this.processActionAsync(action, bTimer, objectBeingActedUpon).then(() => {
            if (callback) callback();
        });
    },

    runEvents: function (EventType: string, done?: () => void): void {
        void this.runEventsAsync(EventType).then(() => {
            if (done) done();
        });
    },

    /**
     * Execute an action with proper async flow.
     * Conditions are tested and their commands executed inline.
     */
    executeActionAsync: async function (act: Action, bTimer?: boolean): Promise<void> {
        (window as any).Logger?.logExecutingAction?.(act);

        // Advanced-mode actions store their behavior entirely as `act.AdvancedScript`
        // and have empty PassCommands/FailCommands/Conditions (both the designer UI's
        // "Switch to Advanced" and the AI's convert_actions_to_advanced clear those
        // arrays when converting). The engine's only OTHER path to run an advanced
        // script is the CT_REGALIA_ADVANCED_SCRIPT command in GameCommands.ts, but
        // nothing anywhere ever constructs/inserts that command into an action's
        // command list, so without this check every advanced action is a silent
        // no-op. objectBeingActedUpon is read the same way GameCommands.ts does,
        // since processActionAsync already pushed it onto the Globals stack.
        if (act.UseAdvancedScript && act.AdvancedScript && act.AdvancedScript.trim().length > 0) {
            const objectBeingActedUpon = (Globals as any).objectBeingActedUpon;
            await (window as any).AdvancedActions?.executeAdvancedAction?.(act, objectBeingActedUpon);
            return;
        }

        if (act.Conditions.length === 0) {
            // No condition
            await GameCommands.runCommandsAsync(act.PassCommands);
        } else {
            let bResult: boolean = act.bConditionFailOnFirst;

            for (const tempcond of act.Conditions) {
                const bPassed = await (window as any).GameConditions?.testConditionAsync?.(tempcond, act, null);
                const commands = bPassed ? tempcond.PassCommands : tempcond.FailCommands;

                await GameCommands.runCommandsAsync(commands);
                if ((Globals as any).endGame) return;

                if (act.bConditionFailOnFirst) {
                    bResult = bPassed;
                    if (!bResult) break;
                } else {
                    bResult = bResult || bPassed;
                }
            }

            const commands = bResult ? act.PassCommands : act.FailCommands;
            await GameCommands.runCommandsAsync(commands);
        }
    },

    /**
     * Process an action with proper async flow and globals stack scoping.
     */
    // objectBeingActedUpon accepts any entity type (RagsObject/Character/Room/
    // Player) — it's stored as-is on the Globals stack and later read back by
    // advanced scripts' `self`/getSelf, which is entity-type-agnostic.
    processActionAsync: async function (action: Action | string, bTimer?: boolean, objectBeingActedUpon?: any): Promise<void> {
        let act: Action | null = null;

        if (getObjectClass(action) == "action" || (action as Action).actionparent != null) {
            act = action as Action;
        } else {  // "action" is an action name
            // FIXME Use objectBeingActedUpon.Actions instead of Globals.curActions (and get
            //       rid of curActions)?
            if ((Globals as any).curActions) {
                for (let i = 0; i < (Globals as any).curActions.length; i++) {
                    if ((Globals as any).curActions[i].name == action) {
                        act = (Globals as any).curActions[i];
                        break;
                    }
                }
            }
        }

        if (!act) {
            console.warn("Action not found:", action);
            return;
        }

        if ((Settings as any).debugEnabled) console.debug(act.name, Finder.actionOwner(act));

        // Push globals for this action context
        const expectedLen = (Globals as any).length;
        const globalsIndex = (Globals as any).store({
            objectBeingActedUpon: objectBeingActedUpon,
            actionBeingTaken: act,
        });

        let canceled = false;
        try {
            if (act.InputType === "None") {
                // No input to wait for
                await this.executeActionAsync(act, bTimer);
                SetBorders();
                return;
            } else if (act.InputType == "Text") {
                const cancellable = act.EnhInputData && act.EnhInputData.bAllowCancel;
                $dom("#textactionchoice").toggleClass('cancellable', cancellable);
                (Globals as any).inputDataObject = act;
                $dom("#textactionMenuTitle").text(PerformTextReplacements(act.CustomChoiceTitle, null));
                $dom("#textactionchoice").css("visibility", "visible");
                const input = $dom("#textactionchoice input").get(0) as HTMLInputElement;
                if (input) input.focus();
                await gameController.startAwaitingInputAsync(objectBeingActedUpon != undefined);
                canceled = Globals.additionalData === null;
                if (!canceled) {
                    await this.executeActionAsync(act, bTimer);
                }
                SetBorders();
                return;
            } else {
                (window as any).GameUI?.clearInputChoices?.();

                function addPortalObjectChoices() {
                    // Real talk: I don't know what this is supposed to do or whether it still works
                    if (TheGame && TheGame.Player.CurrentRoom != null) {
                        const currentroom = Finder.room(TheGame.Player.CurrentRoom);
                        if (!currentroom) {
                            return;
                        }

                        currentroom.Exits.forEach((roomExit) => {
                            if (roomExit.PortalObjectName != "<None>") {
                                const tempobj = Finder.object(roomExit.PortalObjectName);
                                if (tempobj && tempobj.bVisible) {
                                    (window as any).GameUI?.addInputChoice?.(act, objectToString(tempobj), tempobj);

                                    if (tempobj.bContainer) {
                                        if (!tempobj.bOpenable || tempobj.bOpen) {
                                            (window as any).GameUI?.addOpenedObjects?.(tempobj, $dom("#inputchoices"), "inputchoices");
                                        }
                                    }
                                }
                            }
                        });
                    }
                }

                if (act.InputType == "Custom") {
                    act.CustomChoices.forEach((customChoice) => {
                        const replacedChoiceText = PerformTextReplacements(customChoice);
                        (window as any).GameUI?.addInputChoice?.(act, replacedChoiceText, replacedChoiceText);
                    });
                } else if (act.InputType == "Character") {
                    (window as any).GameUI?.addCharacterOptions?.(act);
                } else if (act.InputType == "Object") {
                    (window as any).GameUI?.addObjectOptions?.(act);
                    addPortalObjectChoices();
                } else if (act.InputType == "Inventory") {
                    Interactables.allVisibleInventoryObjects().forEach((obj) => {
                        (window as any).GameUI?.addInputChoice?.(act, objectToString(obj), obj);
                    });
                } else if (act.InputType == "ObjectOrCharacter") {
                    addPortalObjectChoices();
                    (window as any).GameUI?.addObjectOptions?.(act);
                    (window as any).GameUI?.addCharacterOptions?.(act, true);
                }

                (window as any).GameUI?.setInputMenuTitle?.(act);
                await gameController.startAwaitingInputAsync(objectBeingActedUpon != undefined);
                canceled = Globals.additionalData === null;
            }

            if (!canceled) {
                await this.executeActionAsync(act, bTimer);
            }
        } finally {
            // Restore globals to previous state (always)
            try {
                while ((Globals as any).length > expectedLen) {
                    (Globals as any).restore();
                }
            } catch (e) { console.error('Error restoring globals in processActionAsync', e); }
        }
    },

    /**
     * Run events for a given event type (async version).
     */
    runEventsAsync: async function (EventType: string): Promise<void> {
        if (!TheGame) return;
        const startingroomid = TheGame.Player.CurrentRoom;
        const curroom = Finder.room(startingroomid);
        // objectBeingActedUpon MUST be passed here (the room/player/character
        // itself) even though these are the entity's OWN event action, not an
        // action taken against some other object. Advanced scripts resolve
        // `self` from this value (via getSelf), and processActionAsync's
        // Globals.store() call explicitly writes `objectBeingActedUpon:
        // undefined` into the new stack frame when the argument is omitted —
        // it does NOT fall back to inheriting the previous frame's value.
        // Omitting it here was silently breaking `self` for every room/
        // player/character-owned event script (e.g. <<On Player Enter>>,
        // <<On Player Leave>>): getSelf would end up resolving to a
        // meaningless {type:null,value:null} envelope with no entity methods
        // attached, so `self.refresh()`/`self.moveToRoom()`/etc. would fail
        // with "not a function" even though basic property reads sometimes
        // happened to look plausible.
        let tempact = curroom ? Finder.action(curroom.Actions, EventType) : undefined;
        if (tempact != null) {
            await GameActions.processActionAsync(tempact, false, curroom);
        }
        tempact = Finder.action(TheGame.Player.Actions, EventType);
        if (tempact != null) {
            await GameActions.processActionAsync(tempact, false, TheGame.Player);
        }

        const isPlayerEnter = EventType.indexOf("Player Enter") != -1;
        const isPlayerLeave = EventType.indexOf("Player Leave") != -1;

        for (const obj of Interactables.roomObjects()) {
            if (isPlayerEnter) {
                if (!obj.bEnterFirstTime) {
                    tempact = Finder.action(obj.Actions, "<<On Player Enter First Time>>");
                    obj.bEnterFirstTime = true;
                    if (tempact != null) {
                        await GameActions.processActionAsync(tempact, false, obj);
                    }
                }
            } else if (isPlayerLeave) {
                if (!obj.bLeaveFirstTime) {
                    tempact = Finder.action(obj.Actions, "<<On Player Leave First Time>>");
                    obj.bLeaveFirstTime = true;
                    if (tempact != null) {
                        await GameActions.processActionAsync(tempact, false, obj);
                    }
                }
            }
            tempact = Finder.action(obj.Actions, EventType);
            if (tempact != null) {
                if (EventType == "<<On Player Enter>>" || EventType == "<<On Player Leave>>") {
                    if (EventType == "<<On Player Enter>>") {
                        if (startingroomid == TheGame.Player.CurrentRoom) {
                            await GameActions.processActionAsync(tempact, false, obj);
                        }
                    } else {
                        await GameActions.processActionAsync(tempact, false, obj);
                    }
                }
            }
            if (obj.bContainer && (!obj.bOpenable || obj.bOpen)) {
                for (const tempobj2 of TheGame.Objects) {
                    if ((tempobj2.locationtype == "LT_IN_OBJECT") && (tempobj2.locationname == obj.UniqueIdentifier)) {
                        if (isPlayerEnter) {
                            if (!tempobj2.bEnterFirstTime) {
                                tempact = Finder.action(tempobj2.Actions, "<<On Player Enter First Time>>");
                                tempobj2.bEnterFirstTime = true;
                                if (tempact != null)
                                    await GameActions.processActionAsync(tempact, false, tempobj2);
                            }
                        } else if (isPlayerLeave) {
                            if (!tempobj2.bLeaveFirstTime) {
                                tempact = Finder.action(tempobj2.Actions, "<<On Player Leave First Time>>");
                                tempobj2.bLeaveFirstTime = true;
                                if (tempact != null) {
                                    await GameActions.processActionAsync(tempact, false, tempobj2);
                                }
                            }
                        }
                        tempact = Finder.action(tempobj2.Actions, EventType);
                        if (tempact != null) {
                            if (EventType == "<<On Player Enter>>" || EventType == "<<On Player Leave>>") {
                                if (EventType == "<<On Player Enter>>") {
                                    if (startingroomid == TheGame.Player.CurrentRoom) {
                                        await GameActions.processActionAsync(tempact, false, tempobj2);
                                    }
                                } else {
                                    await GameActions.processActionAsync(tempact, false, tempobj2);
                                }
                            }
                        }
                    }
                }
            }
        }

        for (const character of Interactables.characters()) {
            if (isPlayerEnter) {
                if (!character.bEnterFirstTime) {
                    tempact = Finder.action(character.Actions, "<<On Player Enter First Time>>");
                    character.bEnterFirstTime = true;
                    if (tempact != null)
                        await GameActions.processActionAsync(tempact, false, character);
                }
            } else if (isPlayerLeave) {
                if (!character.bLeaveFirstTime) {
                    tempact = Finder.action(character.Actions, "<<On Player Leave First Time>>");
                    character.bLeaveFirstTime = true;
                    if (tempact != null)
                        await GameActions.processActionAsync(tempact, false, character);
                }
            }
            tempact = Finder.action(character.Actions, EventType);
            if (tempact != null) {
                if (EventType == "<<On Player Enter>>" || EventType == "<<On Player Leave>>") {
                    if (EventType == "<<On Player Enter>>") {
                        if (startingroomid == TheGame.Player.CurrentRoom) {
                            await GameActions.processActionAsync(tempact, undefined, character);
                        }
                    } else
                        await GameActions.processActionAsync(tempact, false, character);
                }
            }
        }
    }
};

// Make available globally
(window as any).GameActions = GameActions;

/**
 * Get object class name (moved from Utilities for forward-dep convenience)
 */
function getObjectClass(obj: any): string | undefined {
    if (obj instanceof (window as any).command) return 'command';

    const ConditionClass = (window as any).ragscondition;
    if (ConditionClass && obj instanceof ConditionClass) return 'ragscondition';

    if (obj instanceof (window as any).action) return 'action';

    if (typeof obj !== 'object') return undefined;

    if (obj && obj.constructor && obj.constructor.toString) {
        const arr = obj.constructor.toString().match(/function\s*(\w+)/);
        if (arr && arr.length == 2) {
            return arr[1];
        }
    }
    return undefined;
}
